import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { type ColumnFiltersState, type PaginationState, type SortingState } from "@tanstack/react-table";
import { useAppSelector } from "../../../../redux/hooks";
import { apiClient } from "../../../../zodios/client";
import { clientToServerFiltering, clientToServerSorting } from "../../../../components/table/utils";
import { useDates } from "../../../../dates/DatesContext";

type Params = {
  pagination: PaginationState;
  sorting: SortingState;
  filters?: ColumnFiltersState;
  search?: string;
};

type V4AdvReportByAdResponse = Awaited<ReturnType<typeof apiClient.getV4AdvReportByAd>>;

export const useGetV4AdvReportByAd = (fetchArchivedAds = false) => {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const { start, end, preStart, preEnd } = useDates();

  return (params: Params) => {
    const cursor = params.pagination.pageIndex * params.pagination.pageSize || undefined;
    const search = params.search ? params.search.split(",") : [];
    return queryOptions({
      queryKey: [
        {
          entity: "V4AdvReportByAd",
          marketPlace,
          params,
          dates: { start, end, preStart, preEnd },
          achived: fetchArchivedAds,
        },
      ],
      queryFn: async () =>
        await apiClient.getV4AdvReportByAd({
          queries: {
            marketplace: marketPlace,
            start_date: start.format("YYYY-MM-DD"),
            end_date: end.format("YYYY-MM-DD"),
            start_date_previous: preStart.format("YYYY-MM-DD"),
            end_date_previous: preEnd.format("YYYY-MM-DD"),
            filters: clientToServerFiltering(params.filters),
            cursor,
            limit: params.pagination.pageSize,
            ordering: clientToServerSorting(params.sorting),
            search: search,
            archived: fetchArchivedAds,
          },
        }),
      placeholderData: keepPreviousData,
    });
  };
};

export const useGetV4AdvReportByAdInfiniteQueryOptions = (params: Params) => {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const { start, end, preStart, preEnd } = useDates();

  const baseParams = {
    ...params,
    pagination: {
      ...params.pagination,
      pageIndex: 0,
    },
  };
  const queryKey = [
    {
      entity: "V4AdvReportByAd",
      scope: "infinite",
      marketPlace,
      params: baseParams,
      dates: { start, end, preStart, preEnd },
    },
  ];
  const searchList = params.search ? params.search.split(",") : [];

  return {
    queryKey,
    queryFn: async ({ pageParam = 0 }: { pageParam?: number }) => {
      const cursor = pageParam * params.pagination.pageSize || undefined;
      return await apiClient.getV4AdvReportByAd({
        queries: {
          marketplace: marketPlace,
          start_date: start.format("YYYY-MM-DD"),
          end_date: end.format("YYYY-MM-DD"),
          start_date_previous: preStart.format("YYYY-MM-DD"),
          end_date_previous: preEnd.format("YYYY-MM-DD"),
          filters: clientToServerFiltering(params.filters),
          cursor,
          limit: params.pagination.pageSize,
          ordering: clientToServerSorting(params.sorting),
          search: searchList,
        },
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage: V4AdvReportByAdResponse | undefined, pages: V4AdvReportByAdResponse[] | undefined) => {
      if (!lastPage?.cursor?.hasNextPage) {
        return undefined;
      }
      return pages?.length;
    },
  };
};

export type AdvertisementQueryV4Row = Awaited<ReturnType<typeof apiClient.getV4AdvReportByAd>>["data"][number];
